import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add,
  People,
  VpnKey,
  BarChart,
  Book,
  Block,
  CheckCircle,
  Refresh
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

function AdminDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [accessCodes, setAccessCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newCode, setNewCode] = useState({
    role: 'student',
    expiresAt: '',
    firstName: '',
    lastName: '',
    middleName: ''
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, codesRes, usersRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/access-codes'),
        api.get('/admin/users')
      ]);

      setStats(statsRes.data);
      setAccessCodes(codesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCode = async () => {
    try {
      setCreating(true);
      await api.post('/admin/access-codes', newCode);
      setDialogOpen(false);
      setNewCode({
        role: 'student',
        expiresAt: '',
        firstName: '',
        lastName: '',
        middleName: ''
      });
      loadData(); // Перезагрузить данные
    } catch (error) {
      console.error('Error creating access code:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleToggleCodeStatus = async (codeId, currentStatus) => {
    try {
      console.log('=== TOGGLE CODE STATUS FRONTEND ===');
      console.log('Code ID:', codeId);
      console.log('Current status:', currentStatus);

      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      console.log('New status:', newStatus);

      const response = await api.put(`/admin/access-codes/${codeId}/status`, { status: newStatus });
      console.log('Response:', response.data);

      loadData();
    } catch (error) {
      console.error('Error updating code status:', error);
      console.error('Error details:', error.response?.data || error.message);
    }
  };

  const handleChangeUserRole = async (userId, newRole) => {
    try {
      await api.put(`/admin/users/${userId}/role`, { role: newRole });
      loadData();
    } catch (error) {
      console.error('Error updating user role:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'warning';
      case 'used': return 'default';
      default: return 'default';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'error';
      case 'author': return 'secondary';
      case 'student': return 'primary';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Панель администратора
          </Typography>
          <Button variant="outlined" onClick={logout}>
            Выйти
          </Button>
        </Box>

        {/* Статистика */}
        {stats && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
          
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Курсов опубликовано
                  </Typography>
                  <Typography variant="h4">
                    {stats.publishedCourses}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Активных кодов
                  </Typography>
                  <Typography variant="h4">
                    {stats.accessCodes.find(code => code.status === 'active')?.count || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Всего курсов
                  </Typography>
                  <Typography variant="h4">
                    {stats.totalCourses}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Управление кодами доступа */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">
              Управление кодами доступа
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setDialogOpen(true)}
            >
              Создать код
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Код</TableCell>
                  <TableCell>ФИО</TableCell>
                  <TableCell>Роль</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Создан</TableCell>
                  <TableCell>Использован</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accessCodes.map((code) => (
                  <TableRow key={code.id}>
                    <TableCell>{code.code}</TableCell>
                    <TableCell>
                      {code.first_name || code.last_name ?
                        `${code.last_name || ''} ${code.first_name || ''} ${code.middle_name || ''}`.trim()
                        : '-'
                      }
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={code.role}
                        color={getRoleColor(code.role)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={code.status}
                        color={getStatusColor(code.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{new Date(code.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>{code.used_by_username || '-'}</TableCell>
                    <TableCell>
                      {code.status !== 'used' && (
                        <Tooltip title={code.status === 'active' ? 'Деактивировать' : 'Активировать'}>
                          <IconButton
                            onClick={() => handleToggleCodeStatus(code.id, code.status)}
                            color={code.status === 'active' ? 'warning' : 'success'}
                          >
                            {code.status === 'active' ? <Block /> : <CheckCircle />}
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Управление пользователями */}
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">
              Управление пользователями
            </Typography>
            <IconButton onClick={loadData}>
              <Refresh />
            </IconButton>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ФИО</TableCell>
                  <TableCell>Имя пользователя</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Роль</TableCell>
                  <TableCell>Код доступа</TableCell>
                  <TableCell>Создан</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((userData) => (
                  <TableRow key={userData.id}>
                    <TableCell>
                      {userData.first_name || userData.last_name ?
                        `${userData.last_name || ''} ${userData.first_name || ''} ${userData.middle_name || ''}`.trim()
                        : '-'
                      }
                    </TableCell>
                    <TableCell>{userData.username}</TableCell>
                    <TableCell>{userData.email || '-'}</TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={userData.role}
                          onChange={(e) => handleChangeUserRole(userData.id, e.target.value)}
                        >
                          <MenuItem value="student">Студент</MenuItem>
                          <MenuItem value="author">Автор</MenuItem>
                          <MenuItem value="admin">Админ</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>{userData.access_code || '-'}</TableCell>
                    <TableCell>{new Date(userData.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {/* Здесь можно добавить дополнительные действия */}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Диалог создания кода */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
          <DialogTitle>Создать код доступа</DialogTitle>
          <DialogContent>
            <FormControl fullWidth margin="normal">
              <InputLabel>Роль</InputLabel>
              <Select
                value={newCode.role}
                onChange={(e) => setNewCode({ ...newCode, role: e.target.value })}
              >
                <MenuItem value="student">Студент</MenuItem>
                <MenuItem value="author">Автор курсов</MenuItem>
                <MenuItem value="admin">Администратор</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              margin="normal"
              label="Фамилия"
              value={newCode.lastName}
              onChange={(e) => setNewCode({ ...newCode, lastName: e.target.value })}
              placeholder="Введите фамилию"
            />

            <TextField
              fullWidth
              margin="normal"
              label="Имя"
              value={newCode.firstName}
              onChange={(e) => setNewCode({ ...newCode, firstName: e.target.value })}
              placeholder="Введите имя"
            />

            <TextField
              fullWidth
              margin="normal"
              label="Отчество (опционально)"
              value={newCode.middleName}
              onChange={(e) => setNewCode({ ...newCode, middleName: e.target.value })}
              placeholder="Введите отчество"
            />

            <TextField
              fullWidth
              margin="normal"
              label="Дата истечения (опционально)"
              type="datetime-local"
              value={newCode.expiresAt}
              onChange={(e) => setNewCode({ ...newCode, expiresAt: e.target.value })}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Отмена</Button>
            <Button
              onClick={handleCreateCode}
              variant="contained"
              disabled={creating}
            >
              {creating ? <CircularProgress size={20} /> : 'Создать'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
}

export default AdminDashboard;
